import { SVGProps, useEffect, useRef } from 'react';

const OFFICIAL_PATHS = [
  'M28.56,188.58c-36.03,0-39.29,195.67-2.21,195.67s38.25-195.67,2.21-195.67Z',
  'M548.68,143.52c-54.19,0-52.44,285.8,3.34,285.8s50.88-285.8-3.34-285.8Z',
  'M425.16,67.13c-13.21,0-22.95,12.12-31.65,43.59s-29.62,62.54-39.4,8.59S323.43,0,295.26,0s-46.69,55.96-59.76,111.03c-13.07,55.08-33.85,55.49-40.92,20.41s-15.7-64.31-36.84-64.31c-67.86,0-73.98,438.58-4.16,438.58,13.93,0,25.12-17.65,33.65-45.62,16.39-51.31,33.33-65.21,42.66-6.21s30.58,118.96,59.52,118.96,45.54-45.78,60.67-112.16c18.88-82.81,34.79-35.32,43.57-7.4s20.62,52.43,35.67,52.43c69.8,0,63.7-438.58-4.16-438.58Z',
];

type Seg = { c1x: number; c1y: number; c2x: number; c2y: number; x: number; y: number };
type Parsed = { x0: number; y0: number; segs: Seg[] };

function parsePath(d: string): Parsed {
  const tokens = d.match(/[MmCcSsZz]|-?\d*\.?\d+/g) ?? [];
  let i = 0;
  let cx = 0;
  let cy = 0;
  let x0 = 0;
  let y0 = 0;
  let prevC2x = 0;
  let prevC2y = 0;
  let hasPrev = false;
  const segs: Seg[] = [];
  const num = (): number => parseFloat(tokens[i++]);
  const isNum = (): boolean => i < tokens.length && /^-?[\d.]/.test(tokens[i]);

  while (i < tokens.length) {
    const t = tokens[i];
    if (t === 'M' || t === 'm') {
      const rel = t === 'm';
      i++;
      cx = x0 = num() + (rel ? cx : 0);
      cy = y0 = num() + (rel ? cy : 0);
      hasPrev = false;
    } else if (t === 'C' || t === 'c') {
      const rel = t === 'c';
      i++;
      while (isNum()) {
        const c1x = num() + (rel ? cx : 0);
        const c1y = num() + (rel ? cy : 0);
        const c2x = num() + (rel ? cx : 0);
        const c2y = num() + (rel ? cy : 0);
        const x = num() + (rel ? cx : 0);
        const y = num() + (rel ? cy : 0);
        segs.push({ c1x, c1y, c2x, c2y, x, y });
        prevC2x = c2x;
        prevC2y = c2y;
        hasPrev = true;
        cx = x;
        cy = y;
      }
    } else if (t === 'S' || t === 's') {
      const rel = t === 's';
      i++;
      while (isNum()) {
        const c1x = hasPrev ? 2 * cx - prevC2x : cx;
        const c1y = hasPrev ? 2 * cy - prevC2y : cy;
        const c2x = num() + (rel ? cx : 0);
        const c2y = num() + (rel ? cy : 0);
        const x = num() + (rel ? cx : 0);
        const y = num() + (rel ? cy : 0);
        segs.push({ c1x, c1y, c2x, c2y, x, y });
        prevC2x = c2x;
        prevC2y = c2y;
        hasPrev = true;
        cx = x;
        cy = y;
      }
    } else {
      i++;
      hasPrev = false;
    }
  }
  return { x0, y0, segs };
}

const PARSED: Parsed[] = OFFICIAL_PATHS.map(parsePath);

const CY = 572.84 / 2;
const WAVELENGTH = 480;
const OMEGA = 5.0;

function deform(p: Parsed, tSec: number, amp: number): string {
  const dy = (x: number, y: number): string =>
    (CY + (y - CY) * (1 + amp * Math.sin((x / WAVELENGTH) * Math.PI * 2 - OMEGA * tSec))).toFixed(
      2,
    );
  let d = `M${p.x0.toFixed(2)},${dy(p.x0, p.y0)}`;
  for (const s of p.segs) {
    d += `C${s.c1x.toFixed(2)},${dy(s.c1x, s.c1y)} ${s.c2x.toFixed(2)},${dy(s.c2x, s.c2y)} ${s.x.toFixed(2)},${dy(s.x, s.y)}`;
  }
  return d + 'Z';
}

const REST_D: string[] = PARSED.map(p => deform(p, 0, 0));

interface InsigniaIconProps extends SVGProps<SVGSVGElement> {
  animated?: boolean;
  variant?: 'on-dark' | 'on-light';
  sizeRatio?: number;
}

export default function InsigniaIcon({
  animated = true,
  variant = 'on-dark',
  sizeRatio = 0.5,
  ...props
}: InsigniaIconProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const pathRefs = useRef<(SVGPathElement | null)[]>([]);
  const sizeRatioRef = useRef(sizeRatio);

  useEffect(() => {
    sizeRatioRef.current = sizeRatio;
  }, [sizeRatio]);

  const isOnDark = variant === 'on-dark';
  const color = isOnDark ? '#ffffff' : '#009f80';

  useEffect(() => {
    if (!animated) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let rafId: number;

    const tick = (t: number) => {
      const tSec = t / 1000;
      const r = Number.isFinite(sizeRatioRef.current) ? sizeRatioRef.current : 0.5;
      const computedAmplitude = 0.18 + Math.max(0, (r - 0.4) / 0.6) * 0.22;
      const amplitude = Number.isFinite(computedAmplitude) ? computedAmplitude : 0.2;
      PARSED.forEach((p, i) => {
        pathRefs.current[i]?.setAttribute('d', deform(p, tSec, amplitude));
      });
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [animated]);

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 591.65 572.84"
      fill={color}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ overflow: 'visible' }}
      {...props}
    >
      {REST_D.map((d, i) => (
        <path
          key={i}
          ref={el => {
            pathRefs.current[i] = el;
          }}
          d={d}
        />
      ))}
    </svg>
  );
}
